import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format, isToday, subMinutes, fromUnixTime } from 'date-fns';
import { task } from 'ember-concurrency';
import type ResourceAbility from 'garaje/abilities/resource';
import type GenericResourceModel from 'garaje/models/generic-resource';
import type FeatureConfigService from 'garaje/services/feature-config';
import type StateService from 'garaje/services/state';

interface AvailableResourcePopupArgs {
  selectedDate: Date;
  selectedTime?: number;
  selectedEndTime?: number;
  featureName: string;
  mapFloorName: string;
  resourceTypeLabel: string;
  hasExistingReservation: boolean;
  onReplaceExistingReservation: () => Promise<void>;
  onBookResource: () => Promise<void>;
  onCancel: () => void;
}

export default class AvailableResourcePopup extends Component<AvailableResourcePopupArgs> {
  @service declare state: StateService;
  @service declare abilities: ResourceAbility;
  @service declare featureConfig: FeatureConfigService;

  @tracked resource: GenericResourceModel | undefined;

  get formattedDate(): string {
    const displayDate = subMinutes(this.args.selectedDate, this.state.minutesBetweenTimezones(this.args.selectedDate));
    return format(displayDate, 'MMMM dd, yyyy');
  }

  get existingReservationDate(): string {
    if (isToday(this.args.selectedDate)) {
      return 'today';
    }

    const displayDate = subMinutes(this.args.selectedDate, this.state.minutesBetweenTimezones(this.args.selectedDate));
    return format(displayDate, 'LLL d');
  }

  get isGRPartialDayEnabled(): boolean {
    return this.featureConfig.isEnabled('partialDayParking');
  }

  get isPartialDay(): boolean {
    return this.isGRPartialDayEnabled && this.args.selectedTime != null && this.args.selectedEndTime != null;
  }

  get formattedStartTime(): string {
    if (!this.isPartialDay) return '';
    const start = fromUnixTime(this.args.selectedTime as number);
    const displayStart = subMinutes(start, this.state.minutesBetweenTimezones(start));
    return format(displayStart, 'h:mm a');
  }

  get formattedEndTime(): string {
    if (!this.isPartialDay) return '';
    const end = fromUnixTime(this.args.selectedEndTime as number);
    const displayEnd = subMinutes(end, this.state.minutesBetweenTimezones(end));
    return format(displayEnd, 'h:mm a');
  }

  onBookResourceTask = task({ drop: true }, async () => {
    await this.args.onBookResource();
  });

  onReplaceExistingReservationTask = task({ drop: true }, async () => {
    await this.args.onReplaceExistingReservation();
  });
}
